import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MessagingService } from "../../../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../../../core/messaging/severity-type.enum";
import { ListItem } from "../../../../../../shared/list/list-item";
import { NumberHelper } from "../../../../../../utilities/contracts/number-helper";
import { AuditPageEntries } from "../../../../member/chase-detail/chase-detail-chart/audit-page-entry/audit-page-entries.model";
import { AuditPageEntryService } from "../../../../member/chase-detail/chase-detail-chart/audit-page-entry/audit-page-entry.service";
import { RiskPageData } from "../../../../member/chase-detail/chase-detail-chart/audit-page-entry/risk-page-data.model";
import { ChaseDetailStateService } from "../../../../member/chase-detail/chase-detail-state.service";
import { ChaseDetailService } from "../../../../member/chase-detail/chase-detail.service";
import { ProjectType } from "../../../../project/project-type.enum";
import { AuditPackageStatus } from "../../../audit-package-status.enum";
import { ChaseDetailForAuditItem } from "../../../chase-detail-for-audit-item.model";
import { AuditDataSharingService } from "../../../services/audit-data-sharing.service";
import { AuditPackageService } from "../../../services/audit-package.service";
import { AuditQueryGridFiltersService } from "../../audit-query-shared/audit-query-grid-filters/audit-query-grid-filters.service";
import { IAuditQueryGridData } from "../../audit-query-shared/audit-query-grid-filters/member-radv-record.model";
import { AuditQueryHeaderService } from "../../audit-query-shared/audit-query-header/audit-query-header.service";
import { AuditQueryChartRiskService } from "../audit-query-chart-risk/audit-query-chart-risk.service";
import { AuditQueryGridRiskService } from "../audit-query-grid-risk/audit-query-grid-risk.service";

@Component({
  selector: "app-audit-query-grid-action-risk",
  template: `
    <app-button class="header-button" [text]="actionText" (onClick)="editSelectAction()" [disabled]="disabled"></app-button>
  `,
  styleUrls: ["./audit-query-grid-action-risk.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditQueryGridActionRiskComponent {
  @Input() data: IAuditQueryGridData;
  @Input() parentComponent: any; // Reference to parent grid component
  @Output() chaseId = new EventEmitter<string>();
  @Output() showWarning = new EventEmitter();

  private unsubscribe = new Subject();

  constructor(
    private readonly chaseService: ChaseDetailService,
    private readonly auditQueryHeaderService: AuditQueryHeaderService,
    private readonly auditQueryChartRiskService: AuditQueryChartRiskService,
    private readonly auditPageEntryService: AuditPageEntryService,
    private readonly auditPackageService: AuditPackageService,
    private readonly auditQueryGridRiskService: AuditQueryGridRiskService,
    private readonly auditQueryGridFiltersService: AuditQueryGridFiltersService,
    private readonly messagingService: MessagingService,
    private chaseDetailStateService: ChaseDetailStateService,
    private readonly auditDataSharingService: AuditDataSharingService
  ) { }

  get actionText(): string {
    return this.data.action.toUpperCase();
  }

  editSelectAction(): void {
    this.getOrCreateAuditPackage().then(auditPackageId => {
      if (auditPackageId) {
        return this.createAuditPackageItemIfNeeded(auditPackageId);
      } else {
        this.messagingService.showToast("Failed to get or create audit package.", SeverityType.ERROR);
      }
    }).then(auditPackageItemId => {
      this.auditQueryChartRiskService.setChaseDetails(null);
      this.auditPageEntryService.editPageEntry(null);
      this.auditQueryHeaderService.changeTab("chart");
      this.getChaseSummaryItems();

      // SetTimeout is used here to allow the previous dom to get destroyed before loading the new dom for chart tab.
      setTimeout(() => {
        const chaseDetailForAudit = this.convertToChaseDetailForAuditItem(this.data);
        if (!NumberHelper.isAvailable(chaseDetailForAudit.auditPackageItemId)) {
            // Assign the newly created auditPackageItemId if it wasn't set
            chaseDetailForAudit.auditPackageItemId = NumberHelper.toNumber(auditPackageItemId);
        }
        this.auditQueryChartRiskService.setChaseDetails(chaseDetailForAudit);
        this.resetAuditPackageItem();
      },         500);

      this.getChaseDetailsForAudit();
    });
  }

  get disabled(): boolean {
    return !this.data.auditReady || this.data.auditReady.toLowerCase() !== "yes";
  }

  private getChaseSummaryItems(): void {
    this.chaseDetailStateService.setData({
      chaseId: this.data.chaseId,
    });

    this.chaseService.getSummaryItems(this.data.chaseId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(items => {
        this.chaseService.chaseDetailsChange.next(items);
      });
  }

  resetAuditPackageItem(): void {
    if (this.actionText === "EDIT") {
      const auditPackageItemId = NumberHelper.toNumber(this.data.auditPackageItemId);
      this.auditPageEntryService.editPageEntry(auditPackageItemId);
    }
  }

  private getChaseDetailsForAudit(): void {
    const chaseDetails = [
      new ListItem({
        key: "MeasureID",
        value: this.data.measureCode,
      }),

      new ListItem({
        key: "Member Name",
        value: `${this.data.memberFirstName} ${this.data.memberLastName}`,
      }),
      new ListItem({
        key: "DOB",
        value: this.data.memberDOB,
      }),
      new ListItem({
        key: "Numerator",
        value: this.data.numeratorName,
      }),
      new ListItem({
        key: "Chase ID",
        value: String(this.data.chaseId),
      }),
    ];
  }

  private async getOrCreateAuditPackage(): Promise<number> {
    const activeAuditPackageId = this.auditDataSharingService.getActiveAuditPackageId();
    if (activeAuditPackageId) {
      this.data.auditPackageId = activeAuditPackageId;
      return activeAuditPackageId;
    }

    if (this.parentComponent) {
      const parentAuditPackageId = this.parentComponent.getActiveAuditPackageId();
      if (parentAuditPackageId) {
        this.data.auditPackageId = parentAuditPackageId;
        return parentAuditPackageId;
      }
    }

    return new Promise((resolve, reject) => {
      this.auditPackageService.getActiveAuditPackageForMember(this.data.memberId)
        .subscribe({
          next: activeAuditPackages => {
            const activeAuditPackage = activeAuditPackages && activeAuditPackages.length > 0 ? activeAuditPackages[0] : null;
            if (activeAuditPackage && activeAuditPackage.auditPackageID) {
              this.data.auditPackageId = activeAuditPackage.auditPackageID;
              resolve(activeAuditPackage.auditPackageID);
            } else {
              this.createAuditPackage().then(auditPackageId => {
                resolve(auditPackageId);
              });
            }
          },
          error: error => {
            this.createAuditPackage().then(auditPackageId => {
              resolve(auditPackageId);
            });
          },
        });
    });
  }

  private async createAuditPackage(): Promise<number> {
    return new Promise((resolve, reject) => {
      const updateAuditData = new ChaseDetailForAuditItem({
        action: "CREATE",
        projectId: this.data.projectId,
        memberId: this.data.memberId,
        entityId: this.data.entityId,
        auditReady: this.data.auditReady,
        status: AuditPackageStatus.ACTIVE,
      });

      this.auditPackageService.createOrGetAuditPackage(updateAuditData).subscribe(
        auditPackageId => {
          this.data.auditPackageId = auditPackageId;
          resolve(auditPackageId);
        },
        error => {
          reject(error);
        }
      );
    });
  }

  private createAuditPackageItemIfNeeded(auditPackageId: number): Promise<number> {
    return new Promise((resolve, reject) => {
      const riskPageData = new RiskPageData({
        entityId: this.data.entityId,
        userRank: this.data.userRank,
        icd: this.data.icdCode,
        hcc: this.data.hccCode,
        vrc: this.data.validationResultCode,
        chaseId: this.data.chaseId,
        finalRank: this.data.finalRank,
        sourceTypeName: this.data.source,
        startDate: this.data.startDate,
        endDate: this.data.endDate,
        cmsModel: this.data.cmsModel,
        reveleerRank: this.data.initialRank,
        reportingStatusName: this.data.reportingStatusName,
        encounterType: this.data.encounterType,
        paymentYear: this.data.paymentYear,
        planId: this.data.planId,
        product: this.data.product,
        hccValidationResult: this.data.hccValidationResult,
      });

      const auditPageEntries = new AuditPageEntries({
        auditPackageItemId: NumberHelper.toNumber(this.data.auditPackageItemId),
        auditPackageId,
        projectId: this.data.projectId,
        chaseId: this.data.chaseId,
        numeratorId: this.data.numeratorId,
        riskPageData,
        projectType: ProjectType.RISK,
        memberId: this.data.memberId,
      });

      this.auditPageEntryService.savePageEntriesForRisk(auditPageEntries).subscribe(
        auditPackageItemId => {
          if (auditPackageItemId) {
            this.data.auditPackageItemId = auditPackageItemId.toString();
          }
          if (this.data.memberId) {
            this.auditQueryGridFiltersService.getMemberRadvCard(this.data.memberId).subscribe({
              next: radvCardData => {
                this.auditQueryGridRiskService.sendMemberRadvCardData(radvCardData);
                setTimeout(() => {
                  this.auditQueryGridRiskService.reloadAuditGridFromState();
                },         0);
                this.navigateToChart();
                this.messagingService.showToast("Audit package item created successfully.", SeverityType.SUCCESS);
                resolve(auditPackageItemId);
              },
              error: () => {
                this.auditQueryGridRiskService.reloadAuditGridFromState();
                this.navigateToChart();
                this.messagingService.showToast("Audit package item created successfully.", SeverityType.SUCCESS);
                resolve(auditPackageItemId);
              },
            });
          } else {
            this.auditQueryGridRiskService.reloadAuditGridFromState();
            this.navigateToChart();
            this.messagingService.showToast("Audit package item created successfully.", SeverityType.SUCCESS);
            resolve(auditPackageItemId);
          }
        },
        err => {
          this.messagingService.showToast("Failed to create audit package item.", SeverityType.ERROR);
          reject(err);
        }
      );
    });
  }

  private navigateToChart(): void {
    this.auditQueryChartRiskService.setChaseDetails(null);
    this.auditPageEntryService.editPageEntry(null);
    this.auditQueryHeaderService.changeTab("chart");
    this.getChaseSummaryItems();

    setTimeout(() => {
      const chaseDetailForAudit = this.convertToChaseDetailForAuditItem(this.data);
      this.auditQueryChartRiskService.setChaseDetails(chaseDetailForAudit);
      this.resetAuditPackageItem();
    },         500);

    this.getChaseDetailsForAudit();
  }

  private convertToChaseDetailForAuditItem(data: IAuditQueryGridData): ChaseDetailForAuditItem {
    return new ChaseDetailForAuditItem({
      action: data.action,
      auditPackageId: data.auditPackageId,
      auditPackageItemId: NumberHelper.toNumber(data.auditPackageItemId),
      auditReady: data.auditReady,
      chaseId: data.chaseId,
      projectId: data.projectId,
      entityId: data.entityId,
      numeratorId: data.numeratorId,
      userRank: data.userRank,
      memberFirstName: data.memberFirstName || "",
      memberLastName: data.memberLastName || "",
      icdCode: data.icdCode,
      hccCode: data.hccCode,
      cmsModel: data.cmsModel,
      encounterType: data.encounterType,
      paymentYear: data.paymentYear,
      planId: data.planId,
      product: data.product,
      hccValidationResult: data.hccValidationResult,
    });
  }

}
